import axios from "axios";
import { getAuthInstance } from "@/plugins/auth";
import { useAuthStore } from "@/stores";

let isHandlingUnauthorized = false;

const LOGIN_ENDPOINT_PATTERNS = [
  /okta\/logout/,
  /okta\/get-logout-link/,
  /okta\/pre-login/,
  /auth\/login/,
  /sanctum\/csrf-cookie/,
];

function currentPath() {
  return (window.location.pathname || "/").replace(/\/+$/, "") || "/";
}

function isAccessDeniedPage() {
  return currentPath() === "/pre-login";
}

function isLoginPage() {
  return currentPath() === "/login";
}

export function handleUnauthorized() {
  if (isHandlingUnauthorized) {
    return;
  }

  // Stay on the access-denied card. A 401 from /auth/user (or anything else)
  // must not steal this page to /okta/logout → /login.
  if (isAccessDeniedPage() || isLoginPage()) {
    return;
  }

  isHandlingUnauthorized = true;

  const auth = getAuthInstance();
  const authStore = useAuthStore();
  const token = authStore.$state.token;

  axios
    .get("/api/okta/logout", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    .then(({ data }) => {
      auth?.logout();
      authStore.$patch({ user: null, token: null });
      localStorage.removeItem("nextUrl");
      window.location.href = data;
    })
    .catch(() => {
      auth?.logout();
      authStore.$patch({ user: null, token: null });
      localStorage.removeItem("nextUrl");
      window.location.href = "/login";
    })
    .finally(() => {
      isHandlingUnauthorized = false;
    });
}

// Attach to every axios instance the app talks to the API with, so a 401
// on ANY request logs the user out instead of relying on each call site's
// .catch() to remember to check the status itself.
export function attachUnauthorizedInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const url = error.config?.url || "";
        const headers = error.config?.headers || {};
        const hadAuthHeader = Boolean(
          headers.Authorization || headers.authorization
        );
        const skip = LOGIN_ENDPOINT_PATTERNS.some((pattern) =>
          pattern.test(url)
        );

        // No bearer token means the user was never authenticated on this
        // page (login / pre-login / persist miss). Do not treat that as
        // a session expiry — an active account still 401s without a token.
        if (!skip && hadAuthHeader) {
          handleUnauthorized();
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
